import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import {
    FaArrowLeft,
    FaArrowRight,
    FaBookOpen,
    FaCheckCircle,
    FaClock,
    FaExclamationTriangle,
    FaGraduationCap,
    FaLock,
    FaRedo,
    FaShieldAlt,
    FaCalculator,
    FaLanguage,
} from "react-icons/fa";
import api from "../api/api";

const BRAND = {
    navy: "#062A4B",
    deepNavy: "#031B35",
    blue: "#063A6B",
    gold: "#F6C944",
    goldLight: "#FFE58A",
    orange: "#C0750D",
    green: "#4FAE22",
    red: "#C62828",
    text: "#172033",
    muted: "#687386",
    border: "#DCE4EE",
    background: "#F4F7FB",
    white: "#FFFFFF",
};

const APPLICATION_NUMBER_KEY =
    "paf_entrance_application_number";

const APPLICATION_KEY =
    "paf_entrance_application";

const STATUS_ENDPOINT =
    "/api/entrance-applications/payment/status/";

const LOGIN_ROUTE = "/";

const MATHEMATICS_ROUTE =
    "/entrance-examination/mathematics";

const ENGLISH_ROUTE =
    "/entrance-examination/english";

const normalizeApplicationNumber = (
    value
) =>
    String(value || "")
        .trim()
        .toUpperCase();

const readStoredApplication = () => {
    try {
        const stored =
            localStorage.getItem(
                APPLICATION_KEY
            );

        if (!stored) {
            return null;
        }

        const parsed =
            JSON.parse(stored);

        if (
            !parsed ||
            typeof parsed !== "object" ||
            Array.isArray(parsed)
        ) {
            return null;
        }

        return parsed;
    } catch (error) {
        console.error(
            "Unable to read stored entrance application:",
            error
        );

        return null;
    }
};

const safeText = (
    value,
    fallback = "—"
) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return fallback;
    }

    if (
        typeof value === "string" ||
        typeof value === "number"
    ) {
        return String(value);
    }

    if (typeof value === "object") {
        return (
            value.name ||
            value.label ||
            value.title ||
            value.display_name ||
            value.academic_year ||
            value.year ||
            value.value ||
            fallback
        );
    }

    return fallback;
};

const getApplicantName = (
    application
) => {
    if (!application) {
        return "Applicant";
    }

    if (application.applicant_name) {
        return safeText(
            application.applicant_name,
            "Applicant"
        );
    }

    const name = [
        application.first_name,
        application.last_name,
    ]
        .filter(Boolean)
        .join(" ")
        .trim();

    return name || "Applicant";
};

const getErrorMessage = (
    error,
    fallback
) => {
    const data =
        error?.response?.data;

    if (data instanceof Blob) {
        return fallback;
    }

    if (typeof data === "string") {
        return data;
    }

    if (data?.message) {
        return safeText(
            data.message,
            fallback
        );
    }

    if (data?.detail) {
        return safeText(
            data.detail,
            fallback
        );
    }

    if (
        data?.errors?.application_number?.[0]
    ) {
        return safeText(
            data.errors.application_number[0],
            fallback
        );
    }

    return fallback;
};

const EntranceSubjects = () => {
    const navigate = useNavigate();

    const storedApplication = useMemo(
        () => readStoredApplication(),
        []
    );

    const initialApplicationNumber =
        normalizeApplicationNumber(
            localStorage.getItem(
                APPLICATION_NUMBER_KEY
            ) ||
                storedApplication?.application_number
        );

    const [
        application,
        setApplication,
    ] = useState(
        storedApplication
    );

    const [
        applicationNumber,
        setApplicationNumber,
    ] = useState(
        initialApplicationNumber
    );

    const [
        paymentStatus,
        setPaymentStatus,
    ] = useState("checking");

    const [
        payment,
        setPayment,
    ] = useState(null);

    const [
        checkingStatus,
        setCheckingStatus,
    ] = useState(false);

    const [
        statusError,
        setStatusError,
    ] = useState("");

    const showAlert = useCallback(
        ({
            icon,
            title,
            text,
            confirmButtonText = "OK",
            confirmButtonColor = BRAND.navy,
        }) =>
            Swal.fire({
                icon,
                title,
                text,
                confirmButtonText,
                confirmButtonColor,
                background: BRAND.white,
                color: BRAND.text,
                allowOutsideClick: false,
                customClass: {
                    popup:
                        "paf-subjects-swal-popup",
                    title:
                        "paf-subjects-swal-title",
                    confirmButton:
                        "paf-subjects-swal-button",
                },
            }),
        []
    );

    const checkPaymentStatus =
        useCallback(
            async ({
                silent = false,
            } = {}) => {
                if (!applicationNumber) {
                    return false;
                }

                if (!silent) {
                    setCheckingStatus(true);
                }

                setStatusError("");

                try {
                    const response =
                        await api.post(
                            STATUS_ENDPOINT,
                            {
                                application_number:
                                    applicationNumber,
                            }
                        );

                    const data =
                        response?.data || {};

                    if (
                        data.application
                    ) {
                        setApplication(
                            data.application
                        );

                        try {
                            localStorage.setItem(
                                APPLICATION_KEY,
                                JSON.stringify(
                                    data.application
                                )
                            );
                        } catch (storageError) {
                            console.error(
                                "Unable to update stored application:",
                                storageError
                            );
                        }
                    }

                    const returnedPayment =
                        data.payment ||
                        null;

                    setPayment(
                        returnedPayment
                    );

                    const paymentExists =
                        Boolean(
                            data.payment_exists
                        );

                    if (!paymentExists) {
                        setPaymentStatus(
                            "not_submitted"
                        );

                        return true;
                    }

                    const status =
                        String(
                            returnedPayment?.status ||
                                data.status ||
                                ""
                        )
                            .trim()
                            .toLowerCase();

                    if (
                        status ===
                            "verified" ||
                        data.is_verified ===
                            true
                    ) {
                        setPaymentStatus(
                            "verified"
                        );
                    } else if (
                        status ===
                            "rejected" ||
                        data.is_rejected ===
                            true
                    ) {
                        setPaymentStatus(
                            "rejected"
                        );
                    } else {
                        setPaymentStatus(
                            "submitted"
                        );
                    }

                    return true;
                } catch (error) {
                    console.error(
                        "Entrance payment status check failed:",
                        error
                    );

                    setStatusError(
                        getErrorMessage(
                            error,
                            "We could not confirm your payment status right now."
                        )
                    );

                    return false;
                } finally {
                    if (!silent) {
                        setCheckingStatus(
                            false
                        );
                    }
                }
            },
            [applicationNumber]
        );

    useEffect(() => {
        if (!applicationNumber) {
            navigate(
                LOGIN_ROUTE,
                {
                    replace: true,
                }
            );

            return;
        }

        checkPaymentStatus();
    }, [
        applicationNumber,
        navigate,
        checkPaymentStatus,
    ]);

    useEffect(() => {
        if (
            !applicationNumber ||
            ![
                "submitted",
                "pending",
            ].includes(paymentStatus)
        ) {
            return undefined;
        }

        const interval =
            window.setInterval(
                () => {
                    checkPaymentStatus({
                        silent: true,
                    });
                },
                12000
            );

        return () =>
            window.clearInterval(
                interval
            );
    }, [
        applicationNumber,
        paymentStatus,
        checkPaymentStatus,
    ]);

    const isVerified =
        paymentStatus ===
        "verified";

    const isWaiting =
        paymentStatus ===
            "submitted" ||
        paymentStatus ===
            "pending";

    const isRejected =
        paymentStatus ===
        "rejected";

    const isNotSubmitted =
        paymentStatus ===
        "not_submitted";

    const applicantName =
        getApplicantName(
            application
        );

    const academicYear =
        safeText(
            application?.academic_year
        );

    const handleBack = () => {
        navigate(-1);
    };

    const handleSubjectClick =
        (subject, route) => {
            if (!isVerified) {
                showAlert({
                    icon:
                        isRejected
                            ? "error"
                            : "warning",
                    title:
                        isRejected
                            ? "Entrance Examination Unavailable"
                            : "Entrance Examination Locked",
                    text:
                        isRejected
                            ? "Your payment was rejected. Please contact the administration office before attempting the entrance examination."
                            : "Your entrance payment must be verified before you can start the entrance examination.",
                    confirmButtonText:
                        "OK",
                    confirmButtonColor:
                        isRejected
                            ? BRAND.red
                            : BRAND.orange,
                });

                return;
            }

            navigate(route, {
                state: {
                    subject,
                    applicationNumber,
                },
            });
        };

    const handleRefresh =
        async () => {
            await checkPaymentStatus();
        };

    const getStatusTitle = () => {
        if (isVerified) {
            return "Entrance Examination Available";
        }

        if (isRejected) {
            return "Payment Requires Attention";
        }

        if (isNotSubmitted) {
            return "Payment Required";
        }

        if (isWaiting) {
            return "Awaiting Payment Verification";
        }

        return "Checking Payment Status";
    };

    const getStatusDescription =
        () => {
            if (isVerified) {
                return "Your payment has been verified. You may now choose the entrance examination you want to take.";
            }

            if (isRejected) {
                return "Your payment has not been approved. The entrance examination will remain locked until the payment issue is resolved.";
            }

            if (isNotSubmitted) {
                return "You must submit your entrance payment before the administration can verify your examination access.";
            }

            if (isWaiting) {
                return "Your payment has been received and is currently awaiting administrative verification. The examination buttons remain locked until verification is complete.";
            }

            return "Please wait while we confirm your entrance payment status.";
        };

    if (!applicationNumber) {
        return null;
    }

    return (
        <div className="paf-subjects-page">
            <style>{`
                * {
                    box-sizing: border-box;
                }

                @keyframes pafSubjectsSpin {
                    to {
                        transform: rotate(360deg);
                    }
                }

                @keyframes pafSubjectsPulse {
                    0%, 100% {
                        box-shadow:
                            0 0 0 0 rgba(192,117,13,.13);
                    }

                    50% {
                        box-shadow:
                            0 0 0 11px rgba(192,117,13,0);
                    }
                }

                @keyframes pafSubjectsGreenPulse {
                    0%, 100% {
                        box-shadow:
                            0 0 0 0 rgba(79,174,34,.13);
                    }

                    50% {
                        box-shadow:
                            0 0 0 11px rgba(79,174,34,0);
                    }
                }

                .paf-subjects-page {
                    min-height: 100vh;
                    padding: 24px 18px 50px;
                    background:
                        linear-gradient(
                            180deg,
                            #F4F7FB 0%,
                            #FFFFFF 50%,
                            #F3F7FB 100%
                        );
                    color: ${BRAND.text};
                }

                .paf-subjects-container {
                    width: 100%;
                    max-width: 1120px;
                    margin: 0 auto;
                }

                .paf-subjects-header {
                    position: relative;
                    overflow: hidden;
                    margin-bottom: 18px;
                    padding: 26px 28px;
                    border-radius: 18px;
                    color: #FFFFFF;
                    background:
                        linear-gradient(
                            120deg,
                            ${BRAND.deepNavy},
                            ${BRAND.blue}
                        );
                    box-shadow:
                        0 15px 38px
                        rgba(6,42,75,.15);
                }

                .paf-subjects-header::before {
                    content: "";
                    position: absolute;
                    width: 220px;
                    height: 220px;
                    right: -90px;
                    top: -120px;
                    border-radius: 50%;
                    background:
                        rgba(246,201,68,.08);
                }

                .paf-subjects-header-content {
                    position: relative;
                    z-index: 1;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                }

                .paf-subjects-brand {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    min-width: 0;
                }

                .paf-subjects-brand-icon {
                    width: 54px;
                    height: 54px;
                    flex-shrink: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 14px;
                    border: 1px solid
                        rgba(255,255,255,.17);
                    background:
                        rgba(255,255,255,.09);
                    color: ${BRAND.gold};
                    font-size: 23px;
                }

                .paf-subjects-eyebrow {
                    margin: 0 0 4px;
                    color: ${BRAND.gold};
                    font-size: 10px;
                    font-weight: 900;
                    letter-spacing: 1.6px;
                    text-transform: uppercase;
                }

                .paf-subjects-title {
                    margin: 0;
                    color: #FFFFFF;
                    font-size: 26px;
                    line-height: 1.2;
                    font-weight: 900;
                }

                .paf-subjects-subtitle {
                    margin: 6px 0 0;
                    color:
                        rgba(255,255,255,.72);
                    font-size: 11px;
                    line-height: 1.5;
                }

                .paf-subjects-back {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 14px;
                    border: 1px solid
                        rgba(255,255,255,.20);
                    border-radius: 9px;
                    background:
                        rgba(255,255,255,.08);
                    color: #FFFFFF;
                    font-size: 10px;
                    font-weight: 900;
                    cursor: pointer;
                    transition: .2s ease;
                }

                .paf-subjects-back:hover {
                    background: #FFFFFF;
                    color: ${BRAND.navy};
                }

                .paf-subjects-application {
                    display: grid;
                    grid-template-columns:
                        minmax(0, 1fr)
                        auto;
                    align-items: center;
                    gap: 18px;
                    margin-bottom: 18px;
                    padding: 17px;
                    border: 1px solid
                        ${BRAND.border};
                    border-radius: 14px;
                    background: #FFFFFF;
                    box-shadow:
                        0 10px 30px
                        rgba(6,42,75,.05);
                }

                .paf-subjects-app-info {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 9px;
                }

                .paf-subjects-app-item {
                    min-width: 145px;
                    padding: 10px 12px;
                    border: 1px solid #EEF2F6;
                    border-radius: 9px;
                    background: #F8FAFC;
                }

                .paf-subjects-label {
                    display: block;
                    margin-bottom: 4px;
                    color: #7B8796;
                    font-size: 8px;
                    font-weight: 900;
                    letter-spacing: .7px;
                    text-transform: uppercase;
                }

                .paf-subjects-value {
                    color: ${BRAND.navy};
                    font-size: 11px;
                    font-weight: 900;
                    overflow-wrap: anywhere;
                }

                .paf-subjects-status {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 7px;
                    min-width: 205px;
                    padding: 11px 14px;
                    border-radius: 9px;
                    background: #FFF7ED;
                    border: 1px solid #FED7AA;
                    color: ${BRAND.orange};
                    font-size: 9px;
                    font-weight: 900;
                }

                .paf-subjects-status.verified {
                    background: #EAF7E5;
                    border-color: #CDE8C4;
                    color: ${BRAND.green};
                }

                .paf-subjects-status.rejected {
                    background: #FEF2F2;
                    border-color: #FECACA;
                    color: ${BRAND.red};
                }

                .paf-subjects-status.checking {
                    background: #F1F5F9;
                    border-color: #DCE4EE;
                    color: ${BRAND.blue};
                }

                .paf-subjects-status-spinner {
                    width: 13px;
                    height: 13px;
                    border: 2px solid
                        rgba(6,58,107,.18);
                    border-top-color:
                        ${BRAND.blue};
                    border-radius: 50%;
                    animation:
                        pafSubjectsSpin
                        .7s linear infinite;
                }

                .paf-subjects-error {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    margin-bottom: 17px;
                    padding: 11px 13px;
                    border: 1px solid #FED7AA;
                    border-radius: 9px;
                    background: #FFF7ED;
                    color: #9A3412;
                    font-size: 10px;
                    font-weight: 800;
                }

                .paf-subjects-error-message {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                }

                .paf-subjects-retry {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 7px 10px;
                    border: 1px solid #E7C89F;
                    border-radius: 7px;
                    background: #FFFFFF;
                    color: #9A3412;
                    font-size: 9px;
                    font-weight: 900;
                    cursor: pointer;
                }

                .paf-subjects-retry:disabled {
                    opacity: .6;
                    cursor: not-allowed;
                }

                .paf-subjects-intro {
                    margin-bottom: 18px;
                    padding: 24px;
                    border: 1px solid
                        ${BRAND.border};
                    border-radius: 15px;
                    background: #FFFFFF;
                    box-shadow:
                        0 10px 30px
                        rgba(6,42,75,.05);
                }

                .paf-subjects-intro-top {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 18px;
                }

                .paf-subjects-intro-icon {
                    width: 46px;
                    height: 46px;
                    flex-shrink: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 12px;
                    background: #F0F5FA;
                    color: ${BRAND.blue};
                    font-size: 20px;
                }

                .paf-subjects-intro-title {
                    margin: 0;
                    color: ${BRAND.navy};
                    font-size: 19px;
                    font-weight: 900;
                }

                .paf-subjects-intro-text {
                    max-width: 760px;
                    margin: 6px 0 0;
                    color: ${BRAND.muted};
                    font-size: 11px;
                    line-height: 1.7;
                }

                .paf-subjects-lock-message {
                    display: flex;
                    align-items: flex-start;
                    gap: 9px;
                    margin-top: 17px;
                    padding: 12px 13px;
                    border: 1px solid #F2D5B0;
                    border-radius: 9px;
                    background: #FFF8EE;
                    color: #8A4A0B;
                    font-size: 10px;
                    line-height: 1.6;
                }

                .paf-subjects-lock-message svg {
                    flex-shrink: 0;
                    margin-top: 2px;
                    color: ${BRAND.orange};
                }

                .paf-subjects-lock-message.verified {
                    border-color: #CDE8C4;
                    background: #F3FAEF;
                    color: #3D6E2A;
                }

                .paf-subjects-lock-message.verified svg {
                    color: ${BRAND.green};
                }

                .paf-subject-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(2, minmax(0, 1fr));
                    gap: 18px;
                }

                .paf-subject-card {
                    position: relative;
                    overflow: hidden;
                    min-width: 0;
                    border: 1px solid
                        ${BRAND.border};
                    border-radius: 16px;
                    background: #FFFFFF;
                    box-shadow:
                        0 10px 30px
                        rgba(6,42,75,.055);
                    transition:
                        transform .22s ease,
                        box-shadow .22s ease,
                        border-color .22s ease;
                }

                .paf-subject-card.available {
                    border-color: #CDE8C4;
                }

                .paf-subject-card.available:hover {
                    transform: translateY(-3px);
                    border-color: #A9D19D;
                    box-shadow:
                        0 16px 36px
                        rgba(6,42,75,.10);
                }

                .paf-subject-card.locked {
                    border-color: #E7D9C8;
                }

                .paf-subject-card::before {
                    content: "";
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: 0;
                    width: 4px;
                    background: ${BRAND.orange};
                }

                .paf-subject-card.available::before {
                    background: ${BRAND.green};
                }

                .paf-subject-card-inner {
                    padding: 22px;
                }

                .paf-subject-card-top {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 15px;
                }

                .paf-subject-icon {
                    width: 54px;
                    height: 54px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 13px;
                    background: #F1F5F9;
                    color: ${BRAND.blue};
                    font-size: 24px;
                }

                .paf-subject-card.available
                .paf-subject-icon {
                    background: #EAF7E5;
                    color: ${BRAND.green};
                }

                .paf-subject-card.locked
                .paf-subject-icon {
                    background: #FFF4E6;
                    color: ${BRAND.orange};
                }

                .paf-subject-lock {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 6px 8px;
                    border-radius: 7px;
                    background: #FFF4E6;
                    color: ${BRAND.orange};
                    font-size: 8px;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: .5px;
                }

                .paf-subject-lock.available {
                    background: #EAF7E5;
                    color: ${BRAND.green};
                }

                .paf-subject-name {
                    margin: 17px 0 5px;
                    color: ${BRAND.navy};
                    font-size: 21px;
                    font-weight: 900;
                }

                .paf-subject-description {
                    min-height: 48px;
                    margin: 0;
                    color: ${BRAND.muted};
                    font-size: 10px;
                    line-height: 1.65;
                }

                .paf-subject-meta {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 7px;
                    margin-top: 15px;
                }

                .paf-subject-meta-item {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    padding: 6px 8px;
                    border: 1px solid #E7EDF3;
                    border-radius: 6px;
                    background: #F8FAFC;
                    color: #657184;
                    font-size: 8px;
                    font-weight: 800;
                }

                .paf-subject-meta-item svg {
                    color: ${BRAND.blue};
                }

                .paf-subject-action {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    margin-top: 19px;
                    padding: 13px 14px;
                    border: none;
                    border-radius: 9px;
                    background:
                        linear-gradient(
                            135deg,
                            ${BRAND.navy},
                            ${BRAND.blue}
                        );
                    color: #FFFFFF;
                    font-size: 10px;
                    font-weight: 900;
                    cursor: pointer;
                    transition:
                        transform .2s ease,
                        box-shadow .2s ease;
                }

                .paf-subject-action:hover {
                    transform: translateY(-1px);
                    box-shadow:
                        0 7px 18px
                        rgba(6,42,75,.16);
                }

                .paf-subject-action.available {
                    background:
                        linear-gradient(
                            135deg,
                            ${BRAND.green},
                            #3D8F1C
                        );
                }

                .paf-subject-action.locked {
                    background:
                        linear-gradient(
                            135deg,
                            #C0750D,
                            #9D5E0A
                        );
                    cursor: not-allowed;
                    opacity: .9;
                }

                .paf-subject-action.locked:hover {
                    transform: none;
                    box-shadow: none;
                }

                .paf-subject-action:disabled {
                    cursor: not-allowed;
                }

                .paf-security-note {
                    display: flex;
                    align-items: flex-start;
                    gap: 8px;
                    margin-top: 18px;
                    padding: 12px 13px;
                    border-radius: 9px;
                    background: #F6F8FB;
                    color: ${BRAND.muted};
                    font-size: 9px;
                    line-height: 1.6;
                }

                .paf-security-note svg {
                    flex-shrink: 0;
                    color: ${BRAND.green};
                    margin-top: 1px;
                }

                .paf-subjects-footer {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    margin-top: 23px;
                    color: #8A94A3;
                    font-size: 9px;
                }

                .paf-subjects-footer svg {
                    color: ${BRAND.green};
                }

                .paf-swal-popup {
                    border-radius: 14px !important;
                }

                .paf-swal-title {
                    font-size: 20px !important;
                }

                .paf-swal-button {
                    border-radius: 8px !important;
                    padding: 10px 18px !important;
                    font-weight: 800 !important;
                }

                @media (max-width: 760px) {
                    .paf-subject-grid {
                        grid-template-columns: 1fr;
                    }

                    .paf-subjects-application {
                        grid-template-columns: 1fr;
                    }

                    .paf-subjects-status {
                        width: 100%;
                    }
                }

                @media (max-width: 600px) {
                    .paf-subjects-page {
                        padding:
                            12px 10px 35px;
                    }

                    .paf-subjects-header {
                        padding:
                            20px 17px;
                        border-radius: 13px;
                    }

                    .paf-subjects-header-content {
                        align-items:
                            flex-start;
                    }

                    .paf-subjects-title {
                        font-size: 20px;
                    }

                    .paf-subjects-subtitle {
                        font-size: 10px;
                    }

                    .paf-subjects-brand-icon {
                        width: 43px;
                        height: 43px;
                        font-size: 19px;
                    }

                    .paf-subjects-back {
                        padding: 8px 9px;
                    }

                    .paf-subjects-back span {
                        display: none;
                    }

                    .paf-subjects-intro {
                        padding: 18px;
                    }

                    .paf-subjects-intro-top {
                        gap: 11px;
                    }

                    .paf-subjects-intro-icon {
                        width: 40px;
                        height: 40px;
                        font-size: 17px;
                    }

                    .paf-subjects-intro-title {
                        font-size: 17px;
                    }

                    .paf-subject-card-inner {
                        padding: 19px;
                    }

                    .paf-subject-name {
                        font-size: 19px;
                    }

                    .paf-subjects-error {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .paf-subjects-retry {
                        width: 100%;
                        justify-content: center;
                    }
                }
            `}</style>

            <div className="paf-subjects-container">
                <header className="paf-subjects-header">
                    <div className="paf-subjects-header-content">
                        <div className="paf-subjects-brand">
                            <div className="paf-subjects-brand-icon">
                                <FaGraduationCap />
                            </div>

                            <div>
                                <div className="paf-subjects-eyebrow">
                                    Passion Aids the Future
                                </div>

                                <h1 className="paf-subjects-title">
                                    Entrance Examination
                                </h1>

                                <p className="paf-subjects-subtitle">
                                    Select the examination
                                    subject you want to take.
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="paf-subjects-back"
                            onClick={
                                handleBack
                            }
                        >
                            <FaArrowLeft />
                            <span>
                                Back
                            </span>
                        </button>
                    </div>
                </header>

                <section className="paf-subjects-application">
                    <div className="paf-subjects-app-info">
                        <div className="paf-subjects-app-item">
                            <span className="paf-subjects-label">
                                Applicant
                            </span>

                            <div className="paf-subjects-value">
                                {
                                    applicantName
                                }
                            </div>
                        </div>

                        <div className="paf-subjects-app-item">
                            <span className="paf-subjects-label">
                                Application Number
                            </span>

                            <div className="paf-subjects-value">
                                {
                                    applicationNumber
                                }
                            </div>
                        </div>

                        <div className="paf-subjects-app-item">
                            <span className="paf-subjects-label">
                                Academic Year
                            </span>

                            <div className="paf-subjects-value">
                                {
                                    academicYear
                                }
                            </div>
                        </div>
                    </div>

                    <div
                        className={`paf-subjects-status ${
                            isVerified
                                ? "verified"
                                : isRejected
                                ? "rejected"
                                : paymentStatus ===
                                  "checking"
                                ? "checking"
                                : ""
                        }`}
                    >
                        {paymentStatus ===
                        "checking" ? (
                            <span className="paf-subjects-status-spinner" />
                        ) : isVerified ? (
                            <FaCheckCircle />
                        ) : isRejected ? (
                            <FaExclamationTriangle />
                        ) : (
                            <FaClock />
                        )}

                        <span>
                            {isVerified
                                ? "Payment Verified"
                                : isRejected
                                ? "Payment Rejected"
                                : paymentStatus ===
                                  "not_submitted"
                                ? "Payment Required"
                                : paymentStatus ===
                                  "checking"
                                ? "Checking Payment..."
                                : "Awaiting Verification"}
                        </span>
                    </div>
                </section>

                {statusError && (
                    <motion.div
                        className="paf-subjects-error"
                        initial={{
                            opacity: 0,
                            y: -5,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                    >
                        <div className="paf-subjects-error-message">
                            <FaExclamationTriangle />

                            <span>
                                {
                                    statusError
                                }
                            </span>
                        </div>

                        <button
                            type="button"
                            className="paf-subjects-retry"
                            onClick={
                                handleRefresh
                            }
                            disabled={
                                checkingStatus
                            }
                        >
                            <FaRedo />

                            {checkingStatus
                                ? "Checking..."
                                : "Refresh Status"}
                        </button>
                    </motion.div>
                )}

                <section className="paf-subjects-intro">
                    <div className="paf-subjects-intro-top">
                        <div className="paf-subjects-intro-icon">
                            <FaBookOpen />
                        </div>

                        <div
                            style={{
                                flex: 1,
                            }}
                        >
                            <h2 className="paf-subjects-intro-title">
                                Choose Your Entrance Subject
                            </h2>

                            <p className="paf-subjects-intro-text">
                                The entrance examination
                                consists of separate
                                Mathematics and English
                                examinations. Select a
                                subject below to begin.
                            </p>
                        </div>
                    </div>

                    {!isVerified && (
                        <div className="paf-subjects-lock-message">
                            <FaLock />

                            <span>
                                <strong>
                                    Examination access is
                                    currently locked.
                                </strong>{" "}
                                Your subject links are
                                intentionally displayed
                                below, but they cannot be
                                opened until your entrance
                                payment has been verified
                                by the administration.
                            </span>
                        </div>
                    )}

                    {isVerified && (
                        <div className="paf-subjects-lock-message verified">
                            <FaCheckCircle />

                            <span>
                                <strong>
                                    Examination access
                                    unlocked.
                                </strong>{" "}
                                Your payment has been
                                verified. You may now
                                select Mathematics or
                                English.
                            </span>
                        </div>
                    )}
                </section>

                <section className="paf-subject-grid">
                    <SubjectCard
                        icon={<FaCalculator />}
                        subject="Mathematics"
                        description="Complete the Mathematics entrance examination by answering the multiple-choice questions within the allocated examination time."
                        available={
                            isVerified
                        }
                        onClick={() =>
                            handleSubjectClick(
                                "mathematics",
                                MATHEMATICS_ROUTE
                            )
                        }
                    />

                    <SubjectCard
                        icon={<FaLanguage />}
                        subject="English Language"
                        description="Complete the English Language entrance examination by answering the multiple-choice questions within the allocated examination time."
                        available={
                            isVerified
                        }
                        onClick={() =>
                            handleSubjectClick(
                                "english",
                                ENGLISH_ROUTE
                            )
                        }
                    />
                </section>

                <div className="paf-security-note">
                    <FaShieldAlt />

                    <span>
                        Your examination access is
                        securely connected to application{" "}
                        <strong>
                            {
                                applicationNumber
                            }
                        </strong>
                        . Payment verification is checked
                        before examination access is granted.
                    </span>
                </div>

                <footer className="paf-subjects-footer">
                    <FaLock />
                    Secure Entrance Examination Portal
                </footer>
            </div>
        </div>
    );
};

const SubjectCard = ({
    icon,
    subject,
    description,
    available,
    onClick,
}) => {
    return (
        <motion.article
            className={`paf-subject-card ${
                available
                    ? "available"
                    : "locked"
            }`}
            initial={{
                opacity: 0,
                y: 12,
            }}
            animate={{
                opacity: 1,
                y: 0,
            }}
            transition={{
                duration: 0.35,
            }}
        >
            <div className="paf-subject-card-inner">
                <div className="paf-subject-card-top">
                    <div className="paf-subject-icon">
                        {icon}
                    </div>

                    <div
                        className={`paf-subject-lock ${
                            available
                                ? "available"
                                : ""
                        }`}
                    >
                        {available ? (
                            <>
                                <FaCheckCircle />
                                Available
                            </>
                        ) : (
                            <>
                                <FaLock />
                                Locked
                            </>
                        )}
                    </div>
                </div>

                <h3 className="paf-subject-name">
                    {subject}
                </h3>

                <p className="paf-subject-description">
                    {description}
                </p>

                <div className="paf-subject-meta">
                    <span className="paf-subject-meta-item">
                        <FaBookOpen />
                        Multiple Choice
                    </span>

                    <span className="paf-subject-meta-item">
                        <FaClock />
                        Timed Examination
                    </span>
                </div>

                <button
                    type="button"
                    className={`paf-subject-action ${
                        available
                            ? "available"
                            : "locked"
                    }`}
                    onClick={
                        onClick
                    }
                    disabled={
                        false
                    }
                    aria-disabled={
                        !available
                    }
                    title={
                        available
                            ? `Start ${subject} entrance examination`
                            : `Payment verification required before starting ${subject}`
                    }
                >
                    {available ? (
                        <>
                            <FaGraduationCap />
                            Start {subject}
                            <FaArrowRight />
                        </>
                    ) : (
                        <>
                            <FaLock />
                            {subject} Locked
                            <FaArrowRight />
                        </>
                    )}
                </button>
            </div>
        </motion.article>
    );
};

export default EntranceSubjects;